// src/App.tsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
import './App.css';

import { useAppDispatch, useAppSelector } from './store/hooks';
import {
  loginRequest,
  signupRequest,
  logoutRequest,
  logoutSuccess,
  restoreSessionRequest,
  guestSessionInitialized,
  sessionExpiredLogout,
} from './store/slice/authSlice';

import { onUnauthorized } from './api/axiosConfig';
import {
  consumeAuthLoginAttempt,
  consumeNotificationOpenAttribution,
  getCurrentPageVisibleMs,
  linkAnalyticsSession,
  markAuthLoginAttempt,
  trackFunnelLandingView,
  trackPageView,
  trackUxEvent,
} from './analytics/analytics';
import StockCategoryPage from './pages/StockCategoryPage';
import PublicStocksLandingPage from './pages/PublicStocksLandingPage';
import StockSeoPage from './pages/StockSeoPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import MainPage from './pages/MainPage';
import FeedPage from './pages/FeedPage';
import ScreenerPage from './pages/ScreenerPage';
import PolicyInsightDashboard from './pages/PolicyInsightDashboard';
import IssueUpdatesPage from './components/issueUpdate/IssueUpdatesPage';
import RealtimeStockScannerPage from './components/realtime/RealtimeStockScannerPage';
import WatchWorkspacePage from './pages/WatchWorkspacePage';
import StockWorkspacePage from './pages/StockWorkspacePage';
import AlertInboxPage from './pages/AlertInboxPage';
import ExplorePage from './pages/ExplorePage';
import WithdrawConfirmDialog from './components/modal/WithdrawConfirmDialog';
import EmailChangeDialog from './components/modal/EmailChangeDialog';
import ConfirmEmailChangePage from './pages/ConfirmEmailChangePage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import LoginSuccessPage from './pages/LoginSuccessPage';
import SocialConsentPage from './pages/SocialConsentPage';
import AdminMonitorPage from './pages/AdminMonitorPage';
import LiveSummaryDock from './components/live/LiveSummaryDock';
import NotificationOnboardingDialog from './components/alerts/NotificationOnboardingDialog';

import MyPage from './components/MyPage';
import FAQList from './components/FAQList';
import AuthModal from './components/modal/AuthModal';
import AccountSettingsModal from './components/modal/AccountSettingsModal';
import ScrollToTopButton from './utils/ScrollToTopButton';
import ScrollToTop from './utils/ScrollToTop';
import Footer from './Footer';
import ProductContextBar from './components/system/ProductContextBar';
import LegalDocumentPage from './pages/LegalDocumentPage';

import BillingPaymentSuccess from './pages/BillingPaymentSuccess';
import BillingPaymentFail from './pages/BillingPaymentFail';
import BillingAuthCallbackPage from './components/billing/BillingAuthCallbackPage';

import DeviceRoute from './routes/DeviceRoute';
import { useAccountSettings } from './main/hooks/useAccountSettings';
import {
  consumeAuthIntent,
  saveAuthIntent,
  type PostLoginPanelTab,
} from './auth/authIntent';

import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
} from '@mui/material';

import {
  MenuRounded,
  CategoryRounded,
  HomeRounded,
  ChevronRightRounded,
  MoreHorizRounded,
  SearchRounded,
  NotificationsActiveRounded,
  PersonRounded,
} from '@mui/icons-material';

const neo = {
  appBg: '#F4F7FA',
  barBg: '#07111F',
  panelBg: '#0B1828',
  border: 'rgba(148,163,184,.20)',
  borderSoft: 'rgba(148,163,184,.12)',
  text: '#F5F8FC',
  textWeak: '#D7E1EC',
  textMuted: '#91A1B4',
  primary: '#8FB2FF',
  primarySolid: '#2968FF',
};


function useIsSmallScreen(breakpointPx = 768) {
  const get = () =>
    typeof window !== 'undefined'
      ? window.matchMedia(`(max-width:${breakpointPx}px)`).matches
      : false;

  const [small, setSmall] = useState<boolean>(get());

  useEffect(() => {
    const mql = window.matchMedia(`(max-width:${breakpointPx}px)`);
    const onChange = (e: MediaQueryListEvent | MediaQueryList) =>
      setSmall('matches' in e ? e.matches : (e as MediaQueryList).matches);

    setSmall(mql.matches);

    if (mql.addEventListener) mql.addEventListener('change', onChange as any);
    else mql.addListener(onChange as any);

    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange as any);
      else mql.removeListener(onChange as any);
    };
  }, [breakpointPx]);

  return small;
}

const normalizePath = (p: string) => {
  if (!p) return '/';
  const noDup = p.replace(/\/{2,}/g, '/');
  return noDup !== '/' ? noDup.replace(/\/+$/, '') : '/';
};

function LegacyMobileRedirect() {
  const location = useLocation();
  const base = normalizePath(location.pathname);
  const legacyMap: Record<string, string> = {
    '/m': '/',
    '/m/home': '/',
    '/m/feeds': '/feeds',
    '/m/stocks': '/stocks',
    '/m/insight': '/insight',
    '/m/interest': '/interest',
    '/m/watch': '/watch',
    '/m/alerts': '/alerts',
    '/m/board': '/board',
    '/m/faq': '/faq',
    '/m/issue': '/issue',
    '/m/reactions': '/reactions',
    '/m/screener': '/screener',
    '/m/explore': '/explore',
    '/m/realtime': '/realtime',
    '/m/mypage': '/mypage',
  };

  const mapped = legacyMap[base];
  const fallback = normalizePath(base.replace(/^\/m(\/|$)/, '/')) || '/';
  const destination = mapped || fallback;

  return (
    <Navigate
      to={`${destination}${location.search || ''}${location.hash || ''}`}
      replace
    />
  );
}

type AuthModalSignupForm = {
  email: string;
  password: string;
  confirmPassword: string;
  phone?: string;

  termsAgree: boolean;
  privacyAgree: boolean;
  disclaimerAgree: boolean;
  age14PlusAgree: boolean;

  marketingEmailAgree: boolean;
  marketingSmsAgree: boolean;

  consentVersions: {
    terms: string;
    privacy: string;
    disclaimer: string;
    ageOver14: string;
    marketing: string;
  };

  turnstileToken: string;
};

type AuthModalReason = 'manual' | 'protected-action' | 'session-expired' | null;

type AuthRequestContext = {
  returnTo: string;
  source: string;
  reason: Exclude<AuthModalReason, null>;
  openPanel: boolean;
  openedAt: number;
  intent?: string;
  intentId?: string;
  stockCode?: string;
  stockName?: string;
};

const PROTECTED_CONTENT_PATHS = [
  '/explore',
  '/m/explore',
  '/feeds',
  '/interest', // legacy redirect to /watch
  '/watch',
  '/m/watch',
  '/stock',
  '/m/stock',
  '/alerts',
  '/insight',
  '/issue',
  '/screener',
  '/realtime',
  '/reactions',
  '/faq',
  '/mypage',
  '/m/feeds',
  '/m/interest',
  '/m/alerts',
  '/m/insight',
  '/m/issue',
  '/m/screener',
  '/m/realtime',
  '/m/reactions',
  '/m/faq',
  '/m/mypage',
];

const isProtectedContentPath = (pathname: string) => {
  const normalized = normalizePath(pathname.split('?')[0].split('#')[0]);
  return PROTECTED_CONTENT_PATHS.some(
    (base) => normalized === base || normalized.startsWith(`${base}/`),
  );
};

function ProtectedContentRoute({
  authenticated,
  checking,
  returnTo,
  onRequireLogin,
  children,
}: {
  authenticated: boolean;
  checking: boolean;
  returnTo: string;
  onRequireLogin: (returnTo: string) => void;
  children: React.ReactElement;
}) {
  const promptedRef = useRef(false);

  useEffect(() => {
    if (checking || authenticated || promptedRef.current) return;
    promptedRef.current = true;
    onRequireLogin(returnTo);
  }, [authenticated, checking, onRequireLogin, returnTo]);

  if (authenticated) return children;

  if (checking) {
    return (
      <Box sx={{ minHeight: { xs: 280, md: 420 }, display: 'grid', placeItems: 'center', px: 2 }}>
        <Typography sx={{ color: neo.textMuted, fontSize: 14, fontWeight: 800 }}>
          로그인 상태를 확인하고 있습니다.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: { xs: 360, md: 520 },
        display: 'grid',
        placeItems: 'center',
        px: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 520,
          p: { xs: 2.4, md: 3.2 },
          borderRadius: 3,
          border: `1px solid ${neo.border}`,
          bgcolor: 'rgba(10,19,32,.96)',
          textAlign: 'center',
        }}
      >
        <Typography sx={{ color: neo.text, fontSize: 21, fontWeight: 950 }}>
          로그인 후 이용할 수 있는 콘텐츠입니다.
        </Typography>
        <Typography
          sx={{
            mt: 1,
            color: neo.textMuted,
            fontSize: 13,
            lineHeight: 1.65,
            wordBreak: 'keep-all',
          }}
        >
          메인과 LiveSummary 맛보기는 로그인 없이 확인할 수 있습니다.
          이슈·스크리너·인텔리전스 차트·FAQ 등 상세 콘텐츠는 로그인 후 바로 이어서 이용합니다.
        </Typography>
        <Button
          variant="contained"
          onClick={() => onRequireLogin(returnTo)}
          sx={{
            mt: 2,
            minHeight: 44,
            px: 3,
            bgcolor: neo.primarySolid,
            fontWeight: 950,
            '&:hover': { bgcolor: '#1f4fd1' },
          }}
        >
          로그인하고 계속 보기
        </Button>
      </Paper>
    </Box>
  );
}

function App() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isSmall = useIsSmallScreen(768);

  const authState = useAppSelector((state) => state.auth);
  const user = authState.user;
  const isAuthChecking = !authState.authInitialized || authState.authStatus === 'AUTH_CHECKING';
  const isAuthenticated = Boolean(
    authState.authInitialized
      && authState.authStatus === 'AUTHENTICATED'
      && authState.isAuthenticated
      && user?.email,
  );

  const [authModalReason, setAuthModalReason] = useState<AuthModalReason>(null);
  const [authRequestContext, setAuthRequestContext] = useState<AuthRequestContext | null>(null);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [notificationOnboardingOpen, setNotificationOnboardingOpen] = useState(false);
  const [notificationOnboardingUserId, setNotificationOnboardingUserId] = useState<number | null>(null);
  const [postLoginPanelPrompt, setPostLoginPanelPrompt] = useState<{
    tab: PostLoginPanelTab;
  } | null>(null);
  const sessionExpiredPromptedRef = useRef(false);
  const postLoginHandledRef = useRef(false);
  const authEntrySourceRef = useRef<string>('');
  const signupAttemptRef = useRef<{ source: string; startedAt: number } | null>(null);
  const signupSuccessTrackedRef = useRef(false);
  const notificationPromptTrackedRef = useRef<string>('');
  const authMethodSelectedRef = useRef(false);
  const firstMainClickTrackedRef = useRef(false);
  const mainEnteredAtRef = useRef<number | null>(null);
  const mainMaxScrollRef = useRef(0);
  const isAuthenticatedRef = useRef(isAuthenticated);

  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    // 최초 진입 직후 auth hydration이 끝나기 전에 guest(false)로 계측되면
    // 실제 회원 재방문도 Guest UV에 중복 집계된다.
    // 인증 판정이 끝난 뒤 한 번만 funnel landing authority를 기록한다.
    if (isAuthChecking) return;
    if (location.pathname !== '/' && location.pathname !== '/m/home') return;

    trackFunnelLandingView({
      device: isSmall ? 'mobile' : 'desktop',
      authenticated: isAuthenticated,
      source: 'landing',
    });
  }, [isAuthChecking, isAuthenticated, isSmall, location.pathname]);

  useEffect(() => {
    const numericUserId = Number(user?.id ?? 0);
    if (!isAuthenticated || !Number.isFinite(numericUserId) || numericUserId <= 0) {
      setNotificationOnboardingOpen(false);
      setNotificationOnboardingUserId(null);
      return;
    }

    try {
      const pendingUserId = Number(sessionStorage.getItem('policy-intel:notification-onboarding-after-login') || 0);
      if (pendingUserId === numericUserId) {
        sessionStorage.removeItem('policy-intel:notification-onboarding-after-login');
        setNotificationOnboardingUserId(numericUserId);
        setNotificationOnboardingOpen(true);
      }
    } catch {
      // Storage restrictions must not affect authentication.
    }
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    if (!notificationOnboardingOpen || !notificationOnboardingUserId) return;

    const loginAt = Number(authState.loginAt ?? 0);
    let fallbackSessionId = '';
    try {
      fallbackSessionId = sessionStorage.getItem('policy-intel:funnel-session-id:v1')?.trim() || '';
    } catch {
      fallbackSessionId = '';
    }

    const loginEpisode = loginAt > 0 ? String(loginAt) : (fallbackSessionId || 'current-session');
    const promptEpisodeKey = `${String(notificationOnboardingUserId)}:${loginEpisode}`;

    if (notificationPromptTrackedRef.current === promptEpisodeKey) return;

    // Cross-remount / cross-tab guard. loginAt changes on a new explicit login,
    // so a later login remains a new measurable onboarding episode.
    const promptStorageKey = `policy-intel:funnel-push-prompt-view:v2:${promptEpisodeKey}`;
    try {
      if (localStorage.getItem(promptStorageKey) === '1') {
        notificationPromptTrackedRef.current = promptEpisodeKey;
        return;
      }
      localStorage.setItem(promptStorageKey, '1');
    } catch {
      // Restricted storage: in-memory episode guard still protects this App lifecycle.
    }

    notificationPromptTrackedRef.current = promptEpisodeKey;
    trackUxEvent('push_prompt_view', {
      source: 'post-login-onboarding',
      auth_trigger: authEntrySourceRef.current || undefined,
      user_id_present: true,
      login_episode: loginEpisode,
    });
  }, [
    authState.loginAt,
    notificationOnboardingOpen,
    notificationOnboardingUserId,
  ]);

  const showAuthModal = authModalReason !== null;

  const openAuthModal = useCallback((reason: Exclude<AuthModalReason, null> = 'manual') => {
    setAuthModalReason(reason);
  }, []);

  const closeAuthModal = useCallback(() => {
    setAuthModalReason(null);
    setAuthRequestContext(null);
    authMethodSelectedRef.current = false;
  }, []);

  const openSidePanel = useCallback(() => {
    trackUxEvent('hamburger_open', {
      device: isSmall ? 'mobile' : 'desktop',
      authenticated: isAuthenticated,
    });
    setSidePanelOpen(true);
  }, [isAuthenticated, isSmall]);

  const closeSidePanel = useCallback(() => {
    setSidePanelOpen(false);
  }, []);

  const requestLoginFor = useCallback((options?: {
    returnTo?: string;
    panelTab?: PostLoginPanelTab;
    reason?: 'manual' | 'protected-action' | 'session-expired';
    openPanel?: boolean;
    source?: string;
    intent?: string;
    intentId?: string;
    stockCode?: string;
    stockName?: string;
  }) => {
    const returnTo =
      options?.returnTo
      || `${location.pathname}${location.search || ''}${location.hash || ''}`;

    const openPanel = options?.openPanel !== false;
    const source = options?.source || 'app';
    const reason = options?.reason || 'manual';
    const context: AuthRequestContext = {
      returnTo,
      source,
      reason,
      openPanel,
      openedAt: Date.now(),
      intent: options?.intent,
      intentId: options?.intentId,
      stockCode: options?.stockCode,
      stockName: options?.stockName,
    };

    authEntrySourceRef.current = source;
    authMethodSelectedRef.current = false;
    setAuthRequestContext(context);

    try {
      sessionStorage.setItem('policy-intel:auth-entry-source', source);
    } catch {
      // 저장소가 제한된 환경에서도 인증 자체는 계속 진행한다.
    }

    saveAuthIntent({
      returnTo,
      openPanel,
      panelTab: options?.panelTab || 'SUMMARY',
      reason,
    });

    const authContextPayload = {
      returnTo,
      source,
      auth_trigger: source,
      reason,
      openPanel,
      authenticated: isAuthenticated,
      intent: context.intent,
      intent_id: context.intentId,
      stock_code: context.stockCode,
      stock_name: context.stockName,
    };

    trackUxEvent('auth_prompt_view', authContextPayload);
    trackUxEvent('auth_modal_open', authContextPayload);

    openAuthModal(reason);
  }, [
    isAuthenticated,
    location.pathname,
    location.search,
    location.hash,
    openAuthModal,
  ]);

  useEffect(() => {
    const handleRequireAuth = (event: Event) => {
      const customEvent = event as CustomEvent<{
        returnTo?: string;
        panelTab?: PostLoginPanelTab;
        source?: string;
        openPanel?: boolean;
        intent?: string;
        intentId?: string;
        stockCode?: string;
        stockName?: string;
      }>;

      if (isAuthenticated) {
        window.dispatchEvent(
          new CustomEvent('policy-intel:live-summary-open', {
            detail: {
              tab: customEvent.detail?.panelTab || 'SUMMARY',
              source: 'already-authenticated',
            },
          }),
        );
        return;
      }

      requestLoginFor({
        returnTo: customEvent.detail?.returnTo,
        panelTab: customEvent.detail?.panelTab || 'SUMMARY',
        reason: 'protected-action',
        openPanel: customEvent.detail?.openPanel,
        source: customEvent.detail?.source || 'live-summary',
        intent: customEvent.detail?.intent,
        intentId: customEvent.detail?.intentId,
        stockCode: customEvent.detail?.stockCode,
        stockName: customEvent.detail?.stockName,
      });
    };

    window.addEventListener('policy-intel:require-auth', handleRequireAuth as EventListener);
    return () =>
      window.removeEventListener('policy-intel:require-auth', handleRequireAuth as EventListener);
  }, [isAuthenticated, requestLoginFor]);

  const authModalPurpose = useMemo(() => {
    const stockName = String(authRequestContext?.stockName || '').trim();
    const intent = String(authRequestContext?.intent || '').trim().toUpperCase();
    const source = String(authRequestContext?.source || '').trim();

    if (intent === 'WATCHLIST' || source === 'main-guest-watchlist') {
      return {
        title: stockName ? `${stockName} 변화를 계속 추적하세요` : '선택한 종목의 변화를 계속 추적하세요',
        description: '로그인 후 방금 선택한 종목의 추적 등록을 이어서 완료합니다.',
      };
    }

    if (intent === 'EVIDENCE_DETAIL' || source === 'main-guest-evidence') {
      return {
        title: stockName ? `${stockName} 근거 상세를 계속 확인하세요` : '방금 확인한 판단 근거를 계속 보세요',
        description: '로그인 후 방금 확인하던 종목의 상세 근거 화면으로 바로 이어집니다.',
      };
    }

    if (authRequestContext?.reason === 'session-expired') {
      return {
        title: '다시 로그인하면 보던 화면부터 이어집니다',
        description: '세션만 다시 연결하고 현재 보던 경로와 요청 의도는 유지합니다.',
      };
    }

    if (authRequestContext?.reason === 'protected-action') {
      return {
        title: '로그인 후 요청한 기능을 바로 이어서 사용합니다',
        description: '로그인 완료 후 현재 요청한 화면과 기능으로 돌아갑니다.',
      };
    }

    return { title: '', description: '' };
  }, [authRequestContext]);

  const handleAuthModalReady = useCallback(() => {
    const context = authRequestContext;
    trackUxEvent('auth_modal_ready', {
      source: context?.source || authEntrySourceRef.current || 'auth-modal',
      auth_trigger: context?.source || authEntrySourceRef.current || 'auth-modal',
      reason: context?.reason || authModalReason || 'manual',
      returnTo: context?.returnTo,
      intent: context?.intent,
      intent_id: context?.intentId,
      stock_code: context?.stockCode,
      stock_name: context?.stockName,
      elapsedMs: context?.openedAt ? Math.max(0, Date.now() - context.openedAt) : undefined,
    });
  }, [authModalReason, authRequestContext]);

  const handleAuthModalDismiss = useCallback((detail: {
    reason: string;
    tab: 'login' | 'signup';
  }) => {
    const context = authRequestContext;
    trackUxEvent('auth_modal_dismiss', {
      source: context?.source || authEntrySourceRef.current || 'auth-modal',
      auth_trigger: context?.source || authEntrySourceRef.current || 'auth-modal',
      reason: context?.reason || authModalReason || 'manual',
      dismiss_reason: detail.reason,
      auth_tab: detail.tab,
      method_selected: authMethodSelectedRef.current,
      returnTo: context?.returnTo,
      intent: context?.intent,
      intent_id: context?.intentId,
      stock_code: context?.stockCode,
      stock_name: context?.stockName,
      elapsedMs: context?.openedAt ? Math.max(0, Date.now() - context.openedAt) : undefined,
    });
  }, [authModalReason, authRequestContext]);

  const handleAuthModalTabChange = useCallback((tab: 'login' | 'signup') => {
    const context = authRequestContext;
    trackUxEvent('auth_modal_tab_change', {
      source: context?.source || authEntrySourceRef.current || 'auth-modal',
      auth_trigger: context?.source || authEntrySourceRef.current || 'auth-modal',
      auth_tab: tab,
      returnTo: context?.returnTo,
      intent: context?.intent,
      stock_code: context?.stockCode,
      stock_name: context?.stockName,
    });
  }, [authRequestContext]);

  const API_BASE_URL =
    window.location.hostname === 'localhost'
      ? 'http://localhost:8080'
      : 'https://www.policy-intel.com';

  const handleGoogleLogin = useCallback(() => {
    const existingIntent = localStorage.getItem('policy-intel:auth-intent');

    if (!existingIntent) {
      saveAuthIntent({
        returnTo: `${location.pathname}${location.search || ''}${location.hash || ''}`,
        openPanel: false,
        panelTab: 'SUMMARY',
        reason: 'social-login',
      });
    }

    let entrySource = authEntrySourceRef.current || authModalReason || 'auth-modal';
    try {
      entrySource = sessionStorage.getItem('policy-intel:auth-entry-source') || entrySource;
    } catch {
      // no-op
    }

    authMethodSelectedRef.current = true;
    markAuthLoginAttempt('google', entrySource);
    trackUxEvent('auth_method_selected', {
      provider: 'google',
      auth_mode: 'login',
      source: entrySource,
      auth_trigger: entrySource,
    });

    trackUxEvent('google_login_click', {
      source: entrySource,
      returnTo: location.pathname,
    });

    window.location.href = `${API_BASE_URL}/api/auth/oauth2/authorize/google`;
  }, [
    API_BASE_URL,
    authModalReason,
    location.pathname,
    location.search,
    location.hash,
  ]);

  const handleNaverLogin = useCallback(() => {
    const existingIntent = localStorage.getItem('policy-intel:auth-intent');

    if (!existingIntent) {
      saveAuthIntent({
        returnTo: `${location.pathname}${location.search || ''}${location.hash || ''}`,
        openPanel: false,
        panelTab: 'SUMMARY',
        reason: 'social-login',
      });
    }

    let entrySource = authEntrySourceRef.current || authModalReason || 'auth-modal';
    try {
      entrySource = sessionStorage.getItem('policy-intel:auth-entry-source') || entrySource;
    } catch {
      // no-op
    }

    authMethodSelectedRef.current = true;
    markAuthLoginAttempt('naver', entrySource);
    trackUxEvent('auth_method_selected', {
      provider: 'naver',
      auth_mode: 'login',
      source: entrySource,
      auth_trigger: entrySource,
    });

    trackUxEvent('naver_login_click', {
      source: entrySource,
      returnTo: location.pathname,
    });

    window.location.href = `${API_BASE_URL}/api/auth/oauth2/authorize/naver`;
  }, [
    API_BASE_URL,
    authModalReason,
    location.pathname,
    location.search,
    location.hash,
  ]);

  const {
    profile,
    accountOpen,
    passwordEditOpen,
    withdrawOpen,
    emailChangeOpen,
    savingAccount,
    changingPassword,
    withdrawing,
    changingEmail,
    successMessage,
    errorMessage,
    socialLoadingProvider,
    loadProfile,
    openAccountSettings,
    closeAccountSettings,
    openPasswordEdit,
    closePasswordEdit,
    closeWithdrawConfirm,
    openWithdrawConfirm,
    openEmailChange,
    closeEmailChange,
    saveAccountSettings,
    changePassword,
    changeEmail,
    connectSocialAccount,
    disconnectSocialAccount,
    confirmWithdraw,
  } = useAccountSettings();

  const resolvedUserEmail = isAuthenticated
    ? String(profile?.email ?? user?.email ?? '')
    : '';

  const isMobileRoute = location.pathname === '/m' || location.pathname.startsWith('/m/');
  const shouldUseMobileLayout = isSmall;
  const isMainRoute = location.pathname === '/';

  const isLoginSuccessRoute = location.pathname === '/login/success';
  const isSocialConsentRoute = location.pathname === '/login/consent';
  const isResetPasswordRoute = location.pathname === '/reset-password';
  const isVerifyEmailRoute = location.pathname === '/verify-email';
  const isConfirmEmailChangeRoute = location.pathname === '/confirm-email-change';

  const isBillingSuccessRoute =
    location.pathname === '/billing/toss-success' ||
    location.pathname === '/billing/toss/success';

  const isBillingFailRoute =
    location.pathname === '/billing/toss-fail' ||
    location.pathname === '/billing/toss/fail';

  const isBillingAuthCallbackRoute =
    location.pathname === '/billing/billing-auth/callback';

  const isLiveSummaryMiniRoute = location.pathname === '/live-summary-mini';
  const isPublicLegalRoute = location.pathname === '/terms' || location.pathname === '/privacy';

  const isPublicAuthUtilityRoute =
    isLoginSuccessRoute ||
    isSocialConsentRoute ||
    isResetPasswordRoute ||
    isVerifyEmailRoute ||
    isConfirmEmailChangeRoute ||
    isBillingSuccessRoute ||
    isBillingFailRoute ||
    isBillingAuthCallbackRoute ||
    isPublicLegalRoute;

  useEffect(() => {
    // Server-issued pi_nt is the verified notification engagement authority.
    // This must run on client-side navigations as well as initial app boot.
    // The raw token is never written to UX analytics.
    consumeNotificationOpenAttribution();

    // The Service Worker adds this marker only for untracked direct push links.
    // It indicates a client-side landing, NOT proof of delivery or a server-confirmed open.
    const url = new URL(window.location.href);
    if (url.searchParams.get('pi_from') !== 'webpush') return;
    url.searchParams.delete('pi_from');
    window.history.replaceState(window.history.state, document.title,
      `${url.pathname}${url.search}${url.hash}`);
    trackUxEvent('return_from_alert', {
      source: 'webpush_client_landing',
      destination: url.pathname,
      attribution_status: 'client_only_unverified',
    });
  }, [location.pathname, location.search]);

  const displayEmail = useMemo(() => {
    const email = resolvedUserEmail;
    if (!email) return '';
    if (!shouldUseMobileLayout) return email;
    return email.length > 14 ? `${email.slice(0, 14)}…` : email;
  }, [resolvedUserEmail, shouldUseMobileLayout]);

  const accountDisplayName = useMemo(() => {
    const nickname = String(
      (profile as any)?.nickname ??
      (profile as any)?.displayName ??
      (profile as any)?.name ??
      (user as any)?.nickname ??
      (user as any)?.displayName ??
      (user as any)?.name ??
      '',
    ).trim();

    return nickname || displayEmail || '내 계정';
  }, [displayEmail, profile, user]);

  const sideNavigate = useCallback(
    (desktopPath: string, _mobilePath: string) => {
      closeSidePanel();
      navigate(desktopPath);
    },
    [navigate, closeSidePanel],
  );

  const onGoStocks = useCallback(
    (category: string, ticker?: string) => {
      const normalizedTicker = String(ticker || '').trim();

      const params = new URLSearchParams();
      params.set('category', category);

      if (normalizedTicker) {
        params.set('ticker', normalizedTicker);
      }

      // Explore and Screener are protected routes. Valid stock selections must
      // consistently land on the v2 decision workspace, not legacy /stocks.
      const validTicker = normalizedTicker.match(/^A?(\d{6})$/)?.[1];
      if (validTicker) {
        navigate(`/stock/${validTicker}`, {
          state: { category, ticker: validTicker, source: 'stock-navigation' },
        });
        return;
      }

      // Preserve the existing category-oriented fallback for non-stock tools.
      navigate(`/stocks?${params.toString()}`, {
        state: {
          category,
          ticker: normalizedTicker || undefined,
          selectedTicker: normalizedTicker || undefined,
        },
      });
    },
    [navigate],
  );

  useEffect(() => {
    if (isPublicAuthUtilityRoute) return;

    // One boot authority: localStorage only decides whether a server restore
    // should be attempted. It never makes the UI authenticated by itself.
    if (authState.authInitialized) return;

    let hasStoredAccessToken = false;

    try {
      hasStoredAccessToken = Boolean(localStorage.getItem('token')?.trim());
    } catch {
      hasStoredAccessToken = false;
    }

    if (hasStoredAccessToken) {
      dispatch(restoreSessionRequest());
      return;
    }

    dispatch(guestSessionInitialized());
  }, [dispatch, isPublicAuthUtilityRoute, authState.authInitialized]);

  useEffect(() => {
    const unsubscribe = onUnauthorized(() => {
      if (isPublicAuthUtilityRoute) return;

      const returnTo = `${location.pathname}${location.search || ''}${location.hash || ''}`;
      dispatch(
        sessionExpiredLogout(
          '로그인 세션이 만료되었습니다. 다시 로그인하면 현재 보던 화면부터 이어집니다.',
        ),
      );

      if (sessionExpiredPromptedRef.current) return;
      sessionExpiredPromptedRef.current = true;
      requestLoginFor({
        returnTo,
        panelTab: 'SUMMARY',
        reason: 'session-expired',
        openPanel: false,
        source: 'session-expired',
      });
    });

    return unsubscribe;
  }, [
    dispatch,
    isPublicAuthUtilityRoute,
    location.pathname,
    location.search,
    location.hash,
    requestLoginFor,
  ]);

  useEffect(() => {
    if (!authState.sessionExpired) {
      if (isAuthenticated) sessionExpiredPromptedRef.current = false;
      return;
    }
    if (isPublicAuthUtilityRoute || sessionExpiredPromptedRef.current) return;

    sessionExpiredPromptedRef.current = true;
    requestLoginFor({
      returnTo: `${location.pathname}${location.search || ''}${location.hash || ''}`,
      panelTab: 'SUMMARY',
      reason: 'session-expired',
      openPanel: false,
      source: 'session-expired',
    });
  }, [
    authState.sessionExpired,
    isAuthenticated,
    isPublicAuthUtilityRoute,
    location.pathname,
    location.search,
    location.hash,
    requestLoginFor,
  ]);

  useEffect(() => {
    if (!isAuthenticated || !user?.email) {
      postLoginHandledRef.current = false;
      setPostLoginPanelPrompt(null);
      setNotificationOnboardingOpen(false);
      setNotificationOnboardingUserId(null);
      return;
    }

    closeAuthModal();
    sessionExpiredPromptedRef.current = false;
    void loadProfile();

    if (!postLoginHandledRef.current) {
      // 로그인 후부터 들어오는 analytics는 Authorization 헤더를 통해
      // 서버 JWT principal의 실제 user_id로 저장한다.
      void linkAnalyticsSession();

      // auth_success는 이제 명시적인 로그인 시도(email/google/naver) 이후에만 기록한다.
      // 단순 세션 복원/페이지 새로고침은 auth_success로 집계하지 않는다.
      const authAttempt = consumeAuthLoginAttempt();
      if (authAttempt) {
        const loginSource = authAttempt.source || authEntrySourceRef.current || 'auth-flow';
        const loginElapsedMs = Math.max(0, Date.now() - authAttempt.startedAt);
        trackUxEvent('auth_success', {
          path: location.pathname,
          provider: authAttempt.provider,
          source: loginSource,
          authMode: 'explicit-login',
          elapsedMs: loginElapsedMs,
        });
        trackUxEvent('login_complete', {
          path: location.pathname,
          provider: authAttempt.provider,
          source: loginSource,
          auth_trigger: loginSource,
          elapsedMs: loginElapsedMs,
        });

        const numericUserId = Number(user?.id ?? 0);
        if (Number.isFinite(numericUserId) && numericUserId > 0) {
          try {
            sessionStorage.setItem('policy-intel:notification-onboarding-after-login', String(numericUserId));
          } catch {
            // ignore
          }
          setNotificationOnboardingUserId(numericUserId);
          setNotificationOnboardingOpen(true);
        }
      }
    }

    if (postLoginHandledRef.current) return;
    postLoginHandledRef.current = true;

    const intent = consumeAuthIntent();

    if (intent) {
      const returnTo = intent.returnTo || `${location.pathname}${location.search || ''}${location.hash || ''}`;

      try {
        sessionStorage.setItem(
          'policy-intel:post-login-live-summary',
          JSON.stringify({
            openPanel: intent.openPanel !== false,
            panelTab: intent.panelTab || 'SUMMARY',
          }),
        );
        localStorage.setItem(
          'policy-intel:auth-event',
          JSON.stringify({ type: 'login', at: Date.now() }),
        );
      } catch {
        // 저장소가 제한된 환경에서도 현재 창 새로고침은 수행한다.
      }

      const currentUrl = `${location.pathname}${location.search || ''}${location.hash || ''}`;
      if (returnTo !== currentUrl) {
        window.location.replace(returnTo);
      } else {
        window.location.reload();
      }
      return;
    }

    try {
      const pendingRaw = sessionStorage.getItem('policy-intel:post-login-live-summary');
      if (!pendingRaw) return;

      sessionStorage.removeItem('policy-intel:post-login-live-summary');
      const pending = JSON.parse(pendingRaw) as {
        openPanel?: boolean;
        panelTab?: PostLoginPanelTab;
      };

      if (pending.openPanel === false) return;

      const panelTab = pending.panelTab || 'SUMMARY';
      setPostLoginPanelPrompt({ tab: panelTab });
      trackUxEvent('post_login_panel_continue_view', {
        panelTab,
        path: location.pathname,
      });
    } catch {
      sessionStorage.removeItem('policy-intel:post-login-live-summary');
    }
  }, [
    isAuthenticated,
    user?.email,
    user?.id,
    loadProfile,
    closeAuthModal,
    location.pathname,
    location.search,
    location.hash,
  ]);

  const continuePostLoginPanel = useCallback(() => {
    if (!postLoginPanelPrompt) return;

    const panelTab = postLoginPanelPrompt.tab;
    setPostLoginPanelPrompt(null);

    trackUxEvent('post_login_panel_continue_click', {
      panelTab,
      path: location.pathname,
    });

    window.dispatchEvent(
      new CustomEvent('policy-intel:live-summary-open', {
        detail: {
          tab: panelTab,
          source: 'post-login-continue',
          refresh: true,
        },
      }),
    );
  }, [location.pathname, postLoginPanelPrompt]);

  useEffect(() => {
    if (isMobileRoute) return;
    trackPageView(location.pathname);
  }, [isMobileRoute, location.pathname]);


  useEffect(() => {
    const isHome = location.pathname === '/';
    if (!isHome || isAuthChecking) return;

    const enteredAt = Date.now();
    mainEnteredAtRef.current = enteredAt;
    mainMaxScrollRef.current = 0;
    firstMainClickTrackedRef.current = false;

    let loadedTracked = false;
    let readyTimeoutTracked = false;
    let hiddenBeforeLoadedTracked = false;
    let exitTracked = false;
    let nestedLoadedFrame = 0;

    const visibleMs = () => getCurrentPageVisibleMs();

    trackUxEvent('main_view', {
      device: isSmall ? 'mobile' : 'desktop',
      authenticated: isAuthenticated,
      referrer: document.referrer || null,
    });

    const loadedFrame = window.requestAnimationFrame(() => {
      nestedLoadedFrame = window.requestAnimationFrame(() => {
        const measuredVisibleMs = visibleMs();
        loadedTracked = true;
        trackUxEvent('main_loaded', {
          loadElapsedMs: measuredVisibleMs,
          visibleMs: measuredVisibleMs,
          wallMs: Math.max(0, Date.now() - enteredAt),
          engagementBasis: 'visible',
        });
      });
    });

    const reachedStayThresholds = new Set<number>();
    const engagementTimer = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;

      const measuredVisibleMs = visibleMs();
      [5, 15, 30].forEach((seconds) => {
        if (reachedStayThresholds.has(seconds)) return;
        if (measuredVisibleMs < seconds * 1000) return;

        reachedStayThresholds.add(seconds);
        trackUxEvent(`main_stay_${seconds}s`, {
          elapsedMs: measuredVisibleMs,
          visibleMs: measuredVisibleMs,
          wallMs: Math.max(0, Date.now() - enteredAt),
          engagementBasis: 'visible',
          maxScrollPct: mainMaxScrollRef.current,
          firstClick: firstMainClickTrackedRef.current,
        });
      });
    }, 500);

    const readyTimer = window.setInterval(() => {
      if (loadedTracked || readyTimeoutTracked || document.visibilityState === 'hidden') return;
      const measuredVisibleMs = visibleMs();
      if (measuredVisibleMs < 8000) return;

      readyTimeoutTracked = true;
      trackUxEvent('main_ready_timeout', {
        visibleMs: measuredVisibleMs,
        elapsedMs: measuredVisibleMs,
        wallMs: Math.max(0, Date.now() - enteredAt),
        engagementBasis: 'visible',
        authenticated: isAuthenticated,
      });
    }, 500);

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'hidden') return;
      if (loadedTracked || hiddenBeforeLoadedTracked) return;

      hiddenBeforeLoadedTracked = true;
      const measuredVisibleMs = visibleMs();
      trackUxEvent('main_hidden_before_loaded', {
        visibleMs: measuredVisibleMs,
        elapsedMs: measuredVisibleMs,
        wallMs: Math.max(0, Date.now() - enteredAt),
        engagementBasis: 'visible',
        authenticated: isAuthenticated,
      });
    };

    const getScrollPercent = () => {
      const root = document.documentElement;
      const scrollTop = window.scrollY || root.scrollTop || 0;
      const scrollable = Math.max(root.scrollHeight - window.innerHeight, 1);
      return Math.min(100, Math.max(0, Math.round((scrollTop / scrollable) * 100)));
    };

    const reached = new Set<number>();
    const handleScroll = () => {
      const pct = getScrollPercent();
      mainMaxScrollRef.current = Math.max(mainMaxScrollRef.current, pct);
      [25, 50, 75].forEach((threshold) => {
        if (pct >= threshold && !reached.has(threshold)) {
          reached.add(threshold);
          const measuredVisibleMs = visibleMs();
          trackUxEvent(`main_scroll_${threshold}`, {
            elapsedMs: measuredVisibleMs,
            visibleMs: measuredVisibleMs,
            engagementBasis: 'visible',
          });
        }
      });
    };

    const handleFirstClick = (event: MouseEvent) => {
      if (firstMainClickTrackedRef.current) return;
      const target = event.target as HTMLElement | null;
      const clickable = target?.closest<HTMLElement>(
        'a,button,[role="button"],[data-analytics-label]',
      );
      if (!clickable) return;

      firstMainClickTrackedRef.current = true;
      const measuredVisibleMs = visibleMs();
      trackUxEvent('main_first_click', {
        elapsedMs: measuredVisibleMs,
        visibleMs: measuredVisibleMs,
        engagementBasis: 'visible',
        target:
          clickable.dataset.analyticsLabel ||
          clickable.getAttribute('aria-label') ||
          clickable.textContent?.trim().slice(0, 80) ||
          clickable.tagName,
      });
    };

    const handleExit = () => {
      if (exitTracked) return;

      const stayMs = visibleMs();
      const wallMs = Math.max(0, Date.now() - enteredAt);

      // StrictMode cleanup 및 background wall-clock을 실제 체류로 오인하지 않는다.
      if (stayMs < 1000) return;

      exitTracked = true;
      trackUxEvent('main_exit', {
        stayMs,
        visibleMs: stayMs,
        wallMs,
        engagementBasis: 'visible',
        maxScrollPct: mainMaxScrollRef.current,
        firstClick: firstMainClickTrackedRef.current,
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('click', handleFirstClick, true);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handleExit);

    return () => {
      window.cancelAnimationFrame(loadedFrame);
      if (nestedLoadedFrame) window.cancelAnimationFrame(nestedLoadedFrame);
      window.clearInterval(engagementTimer);
      window.clearInterval(readyTimer);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleFirstClick, true);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handleExit);

      if (mainEnteredAtRef.current === enteredAt) {
        handleExit();
        mainEnteredAtRef.current = null;
      }
    };
  }, [isAuthChecking, isAuthenticated, isSmall, location.pathname]);

  useEffect(() => {
    const handleAuthEvent = (event: StorageEvent) => {
      if (event.key !== 'policy-intel:auth-event' || !event.newValue) return;

      let eventType = '';
      try {
        const payload = JSON.parse(event.newValue) as { type?: string };
        eventType = payload.type || '';
      } catch {
        return;
      }

      if (eventType === 'login') {
        window.location.reload();
        return;
      }

      if (eventType !== 'logout') return;

      // The originating tab broadcasts only after Web Push cleanup and logout finish.
      // This tab must clear its in-memory Redux auth state as well as shared storage.
      dispatch(logoutSuccess());

      closeAuthModal();
      closeSidePanel();
      closeAccountSettings();
      closePasswordEdit();
      closeWithdrawConfirm();

      if (isLiveSummaryMiniRoute && window.opener && !window.opener.closed) {
        window.close();
        return;
      }

      navigate(isSmall ? '/m/home' : '/', { replace: true });
    };

    window.addEventListener('storage', handleAuthEvent);
    return () => window.removeEventListener('storage', handleAuthEvent);
  }, [
    closeAccountSettings,
    closeAuthModal,
    closePasswordEdit,
    closeSidePanel,
    closeWithdrawConfirm,
    dispatch,
    isLiveSummaryMiniRoute,
    isSmall,
    navigate,
  ]);

  const handleLogin = (form: { email: string; password: string }) => {
    let entrySource = authEntrySourceRef.current || authModalReason || 'auth-modal';
    try {
      entrySource = sessionStorage.getItem('policy-intel:auth-entry-source') || entrySource;
    } catch {
      // no-op
    }

    authMethodSelectedRef.current = true;
    markAuthLoginAttempt('email', entrySource);
    trackUxEvent('auth_method_selected', {
      provider: 'email',
      auth_mode: 'login',
      source: entrySource,
      auth_trigger: entrySource,
    });

    trackUxEvent('email_login_submit', {
      source: entrySource,
      returnTo: location.pathname,
    });

    const existingIntent = localStorage.getItem('policy-intel:auth-intent');
    if (!existingIntent) {
      saveAuthIntent({
        returnTo: `${location.pathname}${location.search || ''}${location.hash || ''}`,
        openPanel: false,
        panelTab: 'SUMMARY',
        reason: 'manual',
      });
    }
    dispatch(loginRequest(form));
  };

  const handleSignup = (form: AuthModalSignupForm) => {
    if (form.password !== form.confirmPassword) {
      alert('비밀번호가 일치하지 않습니다.');
      return;
    }

    let entrySource = authEntrySourceRef.current || authModalReason || 'auth-modal';
    try {
      entrySource = sessionStorage.getItem('policy-intel:auth-entry-source') || entrySource;
    } catch {
      // no-op
    }
    signupAttemptRef.current = { source: entrySource, startedAt: Date.now() };
    signupSuccessTrackedRef.current = false;
    authMethodSelectedRef.current = true;
    trackUxEvent('auth_method_selected', {
      provider: 'email',
      auth_mode: 'signup',
      source: entrySource,
      auth_trigger: entrySource,
    });

    dispatch(
      signupRequest({
        email: form.email,
        password: form.password,
        phone: form.phone ?? '',

        agreeTerms: form.termsAgree,
        agreePrivacy: form.privacyAgree,
        agreeDisclaimer: form.disclaimerAgree,
        ageOver14Agree: form.age14PlusAgree,

        marketingEmailAgree: form.marketingEmailAgree,
        marketingSmsAgree: form.marketingSmsAgree,

        consentVersions: {
          terms: form.consentVersions.terms,
          privacy: form.consentVersions.privacy,
          disclaimer: form.consentVersions.disclaimer,
          ageOver14: form.consentVersions.ageOver14,
          marketing: form.consentVersions.marketing,
        },

        turnstileToken: form.turnstileToken,
      }),
    );
  };

  useEffect(() => {
    if (authState.signupStatus === 'pending') {
      signupSuccessTrackedRef.current = false;
      return;
    }
    if (authState.signupStatus !== 'success' || signupSuccessTrackedRef.current) return;

    signupSuccessTrackedRef.current = true;
    const attempt = signupAttemptRef.current;
    const source = attempt?.source || authEntrySourceRef.current || 'auth-modal';
    const elapsedMs = attempt ? Math.max(0, Date.now() - attempt.startedAt) : undefined;

    // 기존 signup_complete는 호환용으로 유지하고, organic-v2 funnel authority인
    // signup_success도 실제 가입 성공 시점에 함께 남긴다.
    trackUxEvent('signup_complete', {
      provider: 'email',
      source,
      auth_trigger: source,
      elapsedMs,
    });
    trackUxEvent('signup_success', {
      provider: 'email',
      source,
      auth_trigger: source,
      signup_flow: 'email',
      elapsedMs,
    });
    signupAttemptRef.current = null;
  }, [authState.signupStatus]);

  const handleLogout = () => {
    dispatch(logoutRequest());
    setPostLoginPanelPrompt(null);
    closeAuthModal();
    closeSidePanel();
    sessionExpiredPromptedRef.current = false;
    closeAccountSettings();
    closePasswordEdit();
    closeWithdrawConfirm();
    // Cross-tab logout is broadcast by authSaga only after Web Push cleanup/logout.

    const homePath = '/';

    // 라이브 서머리 독립 팝업에서 로그아웃한 경우 창을 먼저 닫는다.
    if (isLiveSummaryMiniRoute && window.opener && !window.opener.closed) {
      window.close();
      window.setTimeout(() => navigate(homePath, { replace: true }), 0);
      return;
    }

    navigate(homePath, { replace: true });
  };


  const navigateFromMenu = useCallback(
    (
      desktopPath: string,
      mobilePath: string,
      label: string,
      source: 'top_nav' | 'bottom_nav' | 'drawer',
    ) => {
      const destination = desktopPath;

      trackUxEvent(`${source}_click`, {
        label,
        destination,
        authenticated: isAuthenticated,
      });

      if (!isAuthenticated && isProtectedContentPath(destination)) {
        closeSidePanel();

        trackUxEvent('protected_route_click', {
          label,
          destination,
          source,
        });

        requestLoginFor({
          returnTo: destination,
          reason: 'protected-action',
          openPanel: false,
          source,
        });
        return;
      }

      sideNavigate(desktopPath, mobilePath);
    },
    [
      closeSidePanel,
      isAuthenticated,
      requestLoginFor,
      sideNavigate,
    ],
  );

  // UI v2: retain existing production routes; change only the product information architecture.
  // The in-app change inbox is separate from notification channel settings.
  const primaryNavigation = [
    { label: '오늘', desktop: '/', mobile: '/m/home', icon: <HomeRounded fontSize="small" />, helper: '지금 확인할 것' },
    { label: '내 종목', desktop: '/watch', mobile: '/m/watch', icon: <CategoryRounded fontSize="small" />, helper: '보유·관심종목' },
    { label: '탐색', desktop: '/explore', mobile: '/m/explore', icon: <SearchRounded fontSize="small" />, helper: '이슈·업종·움직임·스크리너' },
    { label: '알림', desktop: '/alerts', mobile: '/m/alerts', icon: <NotificationsActiveRounded fontSize="small" />, helper: '내 종목 변화 · 확인' },
  ];
  const secondaryNavigation = [
    { label: '전문 스크리너', desktop: '/screener', mobile: '/m/screener' },
    { label: '인텔리전스 차트', desktop: '/stocks', mobile: '/m/stocks' },
    { label: '공시·이벤트 원문', desktop: '/issue', mobile: '/m/issue' },
  ];
  const activeNavigationValue = (() => {
    const current = normalizePath(location.pathname);
    const matched = primaryNavigation.find((item) =>
      (item.desktop === '/explore' && ['/screener', '/issue', '/feeds', '/insight'].includes(current)) ||
      (item.desktop === '/watch' && current.startsWith('/stock/')) ||
      current === item.desktop.split('?')[0] ||
      (item.desktop.startsWith('/mypage') && current.startsWith('/mypage')) ||
      current === item.mobile.split('?')[0]
    );
    return matched?.desktop ?? false;
  })();
  const showWorkspaceSidebar = isAuthenticated && !isPublicAuthUtilityRoute && !isLiveSummaryMiniRoute &&
    !location.pathname.startsWith('/admin') && !location.pathname.startsWith('/stocks/');

  const ProductSidebar = (
    <Box component="aside" aria-label="PolicyIntel 주요 탐색"
      sx={{ width: 228, flexShrink: 0, bgcolor: '#ffffff', borderRight: '1px solid #E2E8F0',
        position: 'sticky', top: 62, alignSelf: 'flex-start', height: 'calc(100vh - 62px)',
        overflowY: 'auto', px: 1.75, pt: 3, pb: 2 }}>
      <Typography sx={{ px: 1.25, mb: 1.2, fontSize: 11, letterSpacing: '.11em', color: '#8190A2', fontWeight: 850 }}>
        YOUR MARKET ASSISTANT
      </Typography>
      <Box component="nav" aria-label="주요 기능" sx={{ display: 'grid', gap: .65 }}>
        {primaryNavigation.map((item) => {
          const current = normalizePath(location.pathname);
          const isActive = (item.desktop === '/explore' && ['/screener', '/issue', '/feeds', '/insight'].includes(current)) ||
            current === item.desktop.split('?')[0] ||
            (item.desktop.startsWith('/mypage') && current.startsWith('/mypage'));
          return <Button key={item.desktop} onClick={() => navigateFromMenu(item.desktop, item.mobile, item.label, 'drawer')}
            startIcon={item.icon} aria-current={isActive ? 'page' : undefined}
            sx={{ justifyContent: 'flex-start', textAlign: 'left', minHeight: 57, px: 1.4,
              borderRadius: '12px', color: isActive ? '#245DDA' : '#334155',
              bgcolor: isActive ? '#EBF2FF' : 'transparent', fontSize: 14, fontWeight: isActive ? 850 : 700,
              '&:hover': { bgcolor: '#F1F5FB' }, '& .MuiButton-startIcon': { mr: 1.35 },
              textTransform: 'none' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', gap: .25 }}>
              <Box>{item.label}</Box>
              <Typography component="span" sx={{ color: isActive ? '#5177B9' : '#8190A2', fontSize: 11, fontWeight: 500 }}>{item.helper}</Typography>
            </Box>
          </Button>;
        })}
      </Box>
      <Divider sx={{ borderColor: '#E2E8F0', my: 2.5 }} />
      <Typography sx={{ px: 1.25, mb: 1.2, fontSize: 11, letterSpacing: '.09em', color: '#8190A2', fontWeight: 850 }}>
        전문 분석 도구
      </Typography>
      <Box sx={{ display: 'grid', gap: .3 }}>
        {secondaryNavigation.map((item) => <Button key={item.desktop}
          onClick={() => navigateFromMenu(item.desktop,item.mobile,item.label,'drawer')}
          sx={{ justifyContent: 'flex-start', borderRadius: '10px', color: '#64748B', fontSize: 12.5,
            py: 1.1, px: 1.5, fontWeight: 650, textTransform: 'none', '&:hover': { bgcolor: '#F1F5FB' } }}>
          {item.label}
        </Button>)}
      </Box>
      <Box sx={{ mt: 3, mx: .6, p: 1.5, bgcolor: '#F6F8FB', borderRadius: 2, border: '1px solid #E2E8F0' }}>
        <Typography sx={{ color: '#475569', fontSize: 12, fontWeight: 750 }}>변화가 확인되면 알려드립니다.</Typography>
        <Typography sx={{ color: '#8391A3', mt: .65, fontSize: 11.5, lineHeight: 1.6 }}>관측하지 못한 원인과 확률은 추정하지 않습니다.</Typography>
      </Box>
    </Box>
  );

  const menuButtonSx = {
    width: { xs: 40, md: 42 },
    height: { xs: 40, md: 42 },
    color: neo.text,
    border: `1px solid ${neo.border}`,
    background: 'rgba(255,255,255,.035)',
    borderRadius: '7px',
    boxShadow: 'none',
    flex: '0 0 auto',
    '&:hover': {
      background: 'rgba(255,255,255,.06)',
      borderColor: neo.primary,
    },
  } as const;

  const drawerItemSx = {
    mx: 1,
    mb: 0.5,
    borderRadius: 2,
    color: neo.textWeak,
    '& .MuiListItemIcon-root': {
      color: neo.primary,
      minWidth: 34,
    },
    '&:hover': {
      background: 'rgba(127,183,255,.08)',
    },
  } as const;

  const SidePanel = (
    <Drawer
      anchor="right"
      open={sidePanelOpen}
      onClose={closeSidePanel}
      PaperProps={{
        sx: {
          width: { xs: 280, sm: 320 },
          background: neo.panelBg,
          color: neo.text,
          borderLeft: `1px solid ${neo.border}`,
          boxShadow: '-8px 0 32px rgba(0,0,0,.34)',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${neo.border}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <img src="/images/policy-intel.png" alt="PolicyIntel 로고" style={{ height: 24 }} />
          <Typography sx={{ fontWeight: 900, color: neo.textWeak, fontSize: 15 }}>
            카테고리
          </Typography>
        </Box>

        <IconButton onClick={closeSidePanel} sx={menuButtonSx}>
          <ChevronRightRounded />
        </IconButton>
      </Box>

      <Box sx={{ px: 2, py: 2 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '.08em', color: neo.textMuted, mb: 1.4 }}>주요 메뉴</Typography>
        <List sx={{ py: 0 }}>
          {primaryNavigation.map((item) => (
            <ListItemButton key={item.desktop} sx={drawerItemSx}
              onClick={() => navigateFromMenu(item.desktop, item.mobile, item.label, 'drawer')}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} secondary={item.helper}
                secondaryTypographyProps={{ sx: { color: 'rgba(234,242,255,.65)', fontSize: 12 } }} />
            </ListItemButton>
          ))}
        </List>
        <Divider sx={{ borderColor: neo.borderSoft, my: 1.8 }} />
        <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '.08em', color: neo.textMuted, mb: 1.4 }}>전문 도구</Typography>
        <List sx={{ py: 0 }}>
          {secondaryNavigation.map((item) => (
            <ListItemButton key={item.desktop} sx={drawerItemSx}
              onClick={() => navigateFromMenu(item.desktop, item.mobile, item.label, 'drawer')}>
              <ListItemText primary={item.label} />
            </ListItemButton>
          ))}
          <ListItemButton sx={drawerItemSx} onClick={() => navigateFromMenu('/faq', '/m/faq', 'FAQ', 'drawer')}>
            <ListItemText primary="도움말·자주 묻는 질문" />
          </ListItemButton>
        </List>
      </Box>

      <Box sx={{ mt: 'auto', p: 2, borderTop: `1px solid ${neo.borderSoft}` }}>
        {isAuthenticated ? (
          <>
            <Button
              fullWidth
              variant="text"
              startIcon={<PersonRounded />}
              onClick={() => {
                closeSidePanel();
                openAccountSettings();
              }}
              data-analytics-label="계정 정보"
              sx={{
                justifyContent: 'flex-start',
                color: neo.textWeak,
                mb: 0.6,
                textTransform: 'none',
                overflow: 'hidden',
              }}
            >
              <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {accountDisplayName}
              </Box>
            </Button>

            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigateFromMenu('/mypage', '/m/mypage', '마이페이지', 'drawer')}
              data-analytics-label="마이페이지"
              sx={{
                color: neo.primary,
                borderColor: neo.border,
                mb: 1,
                fontWeight: 900,
              }}
            >
              마이페이지
            </Button>

            <Button
              fullWidth
              variant="text"
              onClick={handleLogout}
              sx={{
                color: neo.textMuted,
              }}
            >
              로그아웃
            </Button>
          </>
        ) : (
          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              closeSidePanel();
              requestLoginFor({ reason: 'manual', panelTab: 'SUMMARY', openPanel: false });
            }}
            sx={{
              bgcolor: neo.primarySolid,
              mb: 1,
            }}
          >
            로그인 / 회원가입
          </Button>
        )}
      </Box>
    </Drawer>
  );

  const MinimalHeader = (
    <AppBar
      className="pi-app-header"
      position="sticky"
      color="transparent"
      elevation={0}
      sx={{
        background: 'rgba(7,17,31,.985)',
                borderBottom: `1px solid ${neo.border}`,
        color: neo.text,
        zIndex: (theme) => theme.zIndex.appBar,
      }}
    >
      <Toolbar
        sx={{
          px: { xs: 1.2, md: 2.2 },
          minHeight: { xs: 58, md: 62 },
          gap: { xs: 1, md: 2 },
        }}
      >
        <Box
          onClick={() =>
            navigateFromMenu('/', '/m/home', '로고 홈', isSmall ? 'bottom_nav' : 'top_nav')
          }
          data-analytics-label="PolicyIntel 로고"
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            gap: 1,
            minWidth: 0,
            flex: { xs: '1 1 auto', md: '0 0 auto' },
          }}
        >
          <img src="/images/policy-intel.png" alt="PolicyIntel 로고" style={{ height: 26 }} />
          <Typography
            sx={{
              fontWeight: 900,
              color: neo.textWeak,
              fontSize: { xs: 13, md: 14 },
              whiteSpace: 'nowrap',
              letterSpacing: '-0.01em',
            }}
          >
            PolicyIntel
          </Typography>
        </Box>

        <Box sx={{ flex: '1 1 auto', minWidth: 0 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, flex: '0 0 auto' }}>
          {!isPublicAuthUtilityRoute && <Button startIcon={<SearchRounded />} onClick={() => {
              trackUxEvent('global_stock_search_open', { source: 'app-shell-v2', authenticated: isAuthenticated });
              navigate(isAuthenticated ? '/explore?view=SCREENER' : '/?focus=stock-search');
            }} aria-label="종목 검색 열기"
            sx={{ minHeight: 38, color: '#E2E8F0', border: '1px solid rgba(148,163,184,.25)',
              borderRadius: 2.3, fontSize: {xs: 12,md: 13}, fontWeight: 750, px: 1.3 }}>
            <Box sx={{ display: {xs:'none',sm:'block'} }}>종목 검색</Box>
          </Button>}

          {(isMainRoute || isPublicLegalRoute) && !isAuthenticated ? (
            <>
              {!isPublicLegalRoute && (
                <Button
                  onClick={() => navigateFromMenu('/faq', '/m/faq', '이용 안내', 'top_nav')}
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  color: neo.textWeak,
                  fontWeight: 800,
                }}
              >
                이용 안내
              </Button>
              )}
              {isPublicLegalRoute ? (
                <Button
                  variant="outlined"
                  onClick={() => navigate('/')}
                  sx={{
                    minHeight: 40,
                    px: { xs: 1.6, sm: 2.2 },
                    borderRadius: 2,
                    color: neo.textWeak,
                    borderColor: neo.border,
                    fontWeight: 900,
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      borderColor: 'rgba(127,183,255,.5)',
                      bgcolor: 'rgba(127,183,255,.08)',
                    },
                  }}
                >
                  홈으로
                </Button>
              ) : (
              <Button
                variant="contained"
                onClick={() => requestLoginFor({ reason: 'manual', panelTab: 'SUMMARY', openPanel: false })}
                data-analytics-label="헤더 로그인"
                sx={{
                  minHeight: 40,
                  px: { xs: 1.6, sm: 2.2 },
                  borderRadius: 2,
                  bgcolor: neo.primarySolid,
                  fontWeight: 950,
                  whiteSpace: 'nowrap',
                  '&:hover': { bgcolor: '#1f4fd1' },
                }}
              >
                로그인
              </Button>
              )}
            </>
          ) : (
            <>
              {isAuthenticated && (
                <>
                  <Button
                    onClick={openAccountSettings}
                    data-analytics-label="계정 정보"
                    startIcon={<PersonRounded />}
                    aria-label="계정 정보 열기"
                    sx={{
                      display: { xs: 'none', md: 'inline-flex' },
                      color: neo.textWeak,
                      border: `1px solid ${neo.border}`,
                      maxWidth: { md: 154, lg: 190 },
                      minHeight: 36,
                      px: 1.15,
                      borderRadius: 1.8,
                      textTransform: 'none',
                      overflow: 'hidden',
                      '&:hover': {
                        bgcolor: 'rgba(127,183,255,.08)',
                        borderColor: 'rgba(127,183,255,.38)',
                      },
                    }}
                  >
                    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {accountDisplayName}
                    </Box>
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={() =>
                      navigateFromMenu('/mypage', '/m/mypage', '마이페이지', 'top_nav')
                    }
                    data-analytics-label="마이페이지"
                    sx={{
                      display: { xs: 'none', lg: 'inline-flex' },
                      minHeight: 36,
                      px: 1.2,
                      borderRadius: 1.8,
                      color: neo.primary,
                      borderColor: neo.border,
                      fontWeight: 900,
                      whiteSpace: 'nowrap',
                      textTransform: 'none',
                      '&:hover': {
                        bgcolor: 'rgba(127,183,255,.08)',
                        borderColor: 'rgba(127,183,255,.38)',
                      },
                    }}
                  >
                    마이페이지
                  </Button>
                </>
              )}

              <IconButton
                onClick={openSidePanel}
                aria-label={isSmall ? '전체 메뉴 열기' : '더보기 메뉴 열기'}
                data-analytics-label="전체 메뉴"
                sx={menuButtonSx}
              >
                {isSmall ? <MenuRounded /> : <MoreHorizRounded />}
              </IconButton>
            </>
          )}
        </Box>
      </Toolbar>

      {SidePanel}
    </AppBar>
  );

  const protectRoute = useCallback(
    (element: React.ReactElement, returnTo: string) => (
      <ProtectedContentRoute
        authenticated={isAuthenticated}
        checking={isAuthChecking}
        returnTo={returnTo}
        onRequireLogin={(target) =>
          requestLoginFor({
            returnTo: target,
            reason: 'protected-action',
            openPanel: false,
            source: 'route-guard',
          })
        }
      >
        {element}
      </ProtectedContentRoute>
    ),
    [isAuthenticated, isAuthChecking, requestLoginFor],
  );

  const desktopLayout = (
    <>
      {MinimalHeader}
      <Box sx={{ display: 'flex', minWidth: 0, alignItems: 'stretch', bgcolor: '#F6F8FB' }}>
        {showWorkspaceSidebar && ProductSidebar}
        <Box sx={{ flex: 1, minWidth: 0 }}>
        <ProductContextBar authenticated={isAuthenticated} />
        <Box
          sx={{
            minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#F6F8FB',
          }}
        >
        <Container maxWidth={false} className="pi-route-container" sx={{ flex: 1 }}>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<MainPage />} />
            <Route path="/terms" element={<LegalDocumentPage type="terms" />} />
            <Route path="/privacy" element={<LegalDocumentPage type="privacy" />} />
            <Route path="/login/success" element={<LoginSuccessPage />} />
            <Route path="/login/consent" element={<SocialConsentPage />} />
            <Route path="/admin/monitor" element={<AdminMonitorPage />} />
            <Route path="/feeds" element={protectRoute(<DeviceRoute desktop={FeedPage} />, '/feeds')} />
            <Route path="/interest" element={<Navigate to="/watch" replace />} />
            <Route path="/watch" element={protectRoute(<WatchWorkspacePage />, '/watch')} />
            <Route path="/alerts" element={protectRoute(<AlertInboxPage />, '/alerts')} />
            <Route path="/stock/:ticker" element={protectRoute(<StockWorkspacePage />, location.pathname + location.search)} />
            <Route path="/insight" element={protectRoute(<PolicyInsightDashboard />, '/insight')} />
            <Route path="/issue" element={protectRoute(<IssueUpdatesPage />, '/issue')} />
            <Route path="/explore" element={protectRoute(<ExplorePage onGoStocks={onGoStocks} />, '/explore')} />
            <Route path="/screener" element={protectRoute(<ScreenerPage onGoStocks={onGoStocks} />, '/screener')} />
            <Route path="/realtime" element={protectRoute(<RealtimeStockScannerPage />, '/realtime')} />
            {/* <Route path="/reactions" element={<ReactionStocksPage />} /> */}
            <Route path="/stocks" element={isAuthenticated ? <DeviceRoute desktop={StockCategoryPage} /> : <PublicStocksLandingPage />} />
            <Route path="/stocks/:ticker" element={<StockSeoPage />} />
            {/* <Route path="/board" element={<BoardListPage />} /> */}
            {/* <Route path="/board/write" element={<BoardWritePage />} /> */}
            {/* <Route path="/board/:id" element={<BoardDetailPage />} /> */}
            <Route path="/faq" element={protectRoute(<FAQList />, '/faq')} />
            <Route path="/mypage/*" element={protectRoute(<MyPage />, `${location.pathname}${location.search || ''}`)} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* 자동결제 카드 인증 복귀 경로 */}
            <Route path="/billing/billing-auth/callback" element={<BillingAuthCallbackPage />} />

            {/* 기존 일반결제 토스 복귀 경로 */}
            <Route path="/billing/toss-success" element={<BillingPaymentSuccess />} />
            <Route path="/billing/toss-fail" element={<BillingPaymentFail />} />

            {/* 기존 호환 경로 유지 */}
            <Route path="/billing/toss/success" element={<BillingPaymentSuccess />} />
            <Route path="/billing/toss/fail" element={<BillingPaymentFail />} />

            <Route path="/confirm-email-change" element={<ConfirmEmailChangePage />} />
            <Route path="/m/*" element={<LegacyMobileRedirect />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <ScrollToTopButton />
        </Container>
          <Footer />
        </Box>
        </Box>
      </Box>
    </>
  );

  const mobileLayout = (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'var(--pi-canvas)',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          minHeight: '100vh',
          bgcolor: 'var(--pi-canvas)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {MinimalHeader}
      <ProductContextBar authenticated={isAuthenticated} />
        <Box component="main" className="pi-route-mobile">
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<MainPage />} />
            <Route path="/feeds" element={protectRoute(<FeedPage />, '/feeds')} />
            {/* <Route path="/reactions" element={<ReactionStocksPage />} /> */}
            <Route path="/stocks" element={isAuthenticated ? <StockCategoryPage /> : <PublicStocksLandingPage />} />
            <Route path="/stocks/:ticker" element={<StockSeoPage />} />
            <Route path="/interest" element={<Navigate to="/watch" replace />} />
            <Route path="/watch" element={protectRoute(<WatchWorkspacePage />, '/watch')} />
            <Route path="/alerts" element={protectRoute(<AlertInboxPage />, '/alerts')} />
            <Route path="/stock/:ticker" element={protectRoute(<StockWorkspacePage />, location.pathname + location.search)} />
            <Route path="/insight" element={protectRoute(<PolicyInsightDashboard />, '/insight')} />
            <Route path="/issue" element={protectRoute(<IssueUpdatesPage />, '/issue')} />
            <Route path="/explore" element={protectRoute(<ExplorePage onGoStocks={onGoStocks} />, '/explore')} />
            <Route path="/screener" element={protectRoute(<ScreenerPage onGoStocks={onGoStocks} />, '/screener')} />
            {/* <Route path="/realtime" element={<RealtimeStockScannerPage />} /> */}
            {/* <Route path="/board" element={<BoardListPage />} /> */}
            {/* <Route path="/board/write" element={<BoardWritePage />} /> */}
            {/* <Route path="/board/:id" element={<BoardDetailPage />} /> */}
            <Route path="/faq" element={protectRoute(<FAQList />, '/faq')} />
            <Route path="/mypage/*" element={protectRoute(<MyPage />, `${location.pathname}${location.search || ''}`)} />
            <Route path="/login/success" element={<LoginSuccessPage />} />
            <Route path="/login/consent" element={<SocialConsentPage />} />
            <Route path="/admin/monitor" element={<AdminMonitorPage />} />

            {/* 자동결제 카드 인증 복귀 경로 */}
            <Route path="/billing/billing-auth/callback" element={<BillingAuthCallbackPage />} />

            {/* 기존 일반결제 토스 복귀 경로 */}
            <Route path="/billing/toss-success" element={<BillingPaymentSuccess />} />
            <Route path="/billing/toss-fail" element={<BillingPaymentFail />} />

            {/* 기존 호환 경로 유지 */}
            <Route path="/billing/toss/success" element={<BillingPaymentSuccess />} />
            <Route path="/billing/toss/fail" element={<BillingPaymentFail />} />

            <Route path="/m/*" element={<LegacyMobileRedirect />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Box>
        <Footer />
        <Paper
          elevation={12}
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: (theme) => theme.zIndex.appBar,
            borderTop: `1px solid ${neo.border}`,
            bgcolor: '#07111F',
                      }}
        >
          <BottomNavigation
            showLabels
            value={activeNavigationValue}
            sx={{
              height: 66,
              bgcolor: 'transparent',
              '& .MuiBottomNavigationAction-root': {
                minWidth: 0,
                px: 0.4,
                color: neo.textMuted,
              },
              '& .MuiBottomNavigationAction-label': {
                fontSize: '12px',
                whiteSpace: 'nowrap',
              },
              '& .Mui-selected': {
                color: neo.primary,
              },
              '& .Mui-selected .MuiBottomNavigationAction-label': {
                fontSize: '12px',
                fontWeight: 900,
              },
            }}
          >
            {primaryNavigation.map((item) => (
              <BottomNavigationAction
                key={item.desktop}
                value={item.desktop}
                label={item.label}
                icon={item.icon}
                data-analytics-label={`하단 메뉴 ${item.label}`}
                onClick={() =>
                  navigateFromMenu(item.desktop, item.mobile, item.label, 'bottom_nav')
                }
              />
            ))}
          </BottomNavigation>
        </Paper>
      </Box>
    </Box>
  );

  if (isLiveSummaryMiniRoute) {
    return (
      <>
        <LiveSummaryDock
          enabled
          defaultOpen
          standalone
          authenticated={isAuthenticated}
          userEmail={resolvedUserEmail}
          onRequireLogin={(tab) =>
            requestLoginFor({
              returnTo: `${location.pathname}${location.search || ''}${location.hash || ''}`,
              panelTab: tab,
              reason: 'protected-action',
              source: 'live-summary',
            })
          }
        />

        <AuthModal
          open={showAuthModal}
          onClose={closeAuthModal}
          onSignup={handleSignup}
          onLogin={handleLogin}
          loading={authState.loading}
          onGoogleLogin={handleGoogleLogin}
          onNaverLogin={handleNaverLogin}
          contextTitle={authModalPurpose.title}
          contextDescription={authModalPurpose.description}
          onReady={handleAuthModalReady}
          onDismiss={handleAuthModalDismiss}
          onTabChange={handleAuthModalTabChange}
        />
      </>
    );
  }

  return (
    <>
      {shouldUseMobileLayout ? mobileLayout : desktopLayout}

      {!isPublicAuthUtilityRoute && isAuthenticated && (
        <NotificationOnboardingDialog
          open={notificationOnboardingOpen}
          userId={notificationOnboardingUserId}
          onClose={() => setNotificationOnboardingOpen(false)}
          onOpenSettings={() => {
            setNotificationOnboardingOpen(false);
            navigate(`${isSmall ? '/m' : ''}/mypage?tab=alerts`);
          }}
        />
      )}

      {!isPublicAuthUtilityRoute && isAuthenticated && postLoginPanelPrompt && (
        <Paper
          elevation={12}
          role="status"
          aria-live="polite"
          sx={{
            position: 'fixed',
            right: { xs: 12, md: 20 },
            bottom: { xs: 82, md: 20 },
            zIndex: (theme) => theme.zIndex.snackbar,
            width: { xs: 'calc(100vw - 24px)', sm: 360 },
            maxWidth: 'calc(100vw - 24px)',
            p: 1.5,
            borderRadius: 2.5,
            border: `1px solid ${neo.border}`,
            bgcolor: '#07111F',
            color: neo.text,
            boxShadow: '0 18px 48px rgba(0,0,0,.34)',
          }}
        >
          <Typography sx={{ fontSize: 13.5, fontWeight: 950 }}>
            로그인 완료 · 요청한 분석을 계속 확인하세요.
          </Typography>
          <Typography
            sx={{
              mt: 0.45,
              color: neo.textMuted,
              fontSize: 11.5,
              lineHeight: 1.55,
              wordBreak: 'keep-all',
            }}
          >
            브라우저 팝업 차단을 피하기 위해 자동으로 새 창을 열지 않습니다.
          </Typography>
          <Box sx={{ mt: 1.15, display: 'flex', gap: 0.8, justifyContent: 'flex-end' }}>
            <Button
              size="small"
              onClick={() => setPostLoginPanelPrompt(null)}
              sx={{ color: neo.textMuted, fontWeight: 850 }}
            >
              나중에
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={continuePostLoginPanel}
              sx={{
                bgcolor: neo.primarySolid,
                fontWeight: 950,
                '&:hover': { bgcolor: '#1f4fd1' },
              }}
            >
              계속 확인
            </Button>
          </Box>
        </Paper>
      )}

      {!isPublicAuthUtilityRoute && (
        <LiveSummaryDock
          enabled
          authenticated={isAuthenticated}
          userEmail={resolvedUserEmail}
          onRequireLogin={(tab) =>
            requestLoginFor({
              panelTab: tab,
              reason: 'protected-action',
              source: 'live-summary',
            })
          }
        />
      )}

      {!isPublicAuthUtilityRoute && (
        <AuthModal
          open={showAuthModal}
          onClose={closeAuthModal}
          onSignup={handleSignup}
          onLogin={handleLogin}
          loading={authState.loading}
          onGoogleLogin={handleGoogleLogin}
          onNaverLogin={handleNaverLogin}
          contextTitle={authModalPurpose.title}
          contextDescription={authModalPurpose.description}
          onReady={handleAuthModalReady}
          onDismiss={handleAuthModalDismiss}
          onTabChange={handleAuthModalTabChange}
        />
      )}

      <AccountSettingsModal
        open={accountOpen}
        onClose={closeAccountSettings}
        userEmail={profile?.email ?? user?.email ?? ''}
        userPhone={profile?.phone ?? ''}
        marketingEmailAgree={Boolean(profile?.marketingEmailAgree)}
        marketingSmsAgree={Boolean(profile?.marketingSmsAgree)}
        connectedProviders={{
          google: Boolean(profile?.googleConnected),
          kakao: Boolean(profile?.kakaoConnected),
          naver: Boolean(profile?.naverConnected),
        }}
        loading={savingAccount}
        error={errorMessage}
        successMessage={successMessage}
        onSave={saveAccountSettings}
        onOpenPasswordChange={openPasswordEdit}
        onWithdraw={openWithdrawConfirm}
        onOpenEmailChange={openEmailChange}
        onConnectSocial={connectSocialAccount}
        onDisconnectSocial={disconnectSocialAccount}
        socialLoadingProvider={socialLoadingProvider}
      />

      <AuthModal
        open={passwordEditOpen}
        onClose={closePasswordEdit}
        mode="password-edit"
        userEmail={profile?.email ?? user?.email ?? ''}
        loading={changingPassword}
        onPasswordUpdate={changePassword}
        onGoogleLogin={handleGoogleLogin}
        onNaverLogin={handleNaverLogin}
      />

      <WithdrawConfirmDialog
        open={withdrawOpen}
        onClose={closeWithdrawConfirm}
        loading={withdrawing}
        userEmail={profile?.email ?? user?.email ?? ''}
        onConfirm={confirmWithdraw}
      />

      <EmailChangeDialog
        open={emailChangeOpen}
        onClose={closeEmailChange}
        currentEmail={profile?.email ?? user?.email ?? ''}
        loading={changingEmail}
        successMessage={successMessage}
        errorMessage={errorMessage}
        onSubmit={changeEmail}
      />
    </>
  );
}

export default App;


